import { apiClient } from './http.api';
import { API_ROUTES } from './ApiRoutes';

/**
 * Events API
 * 
 * API client for managing events.
 * All event-related API calls go through this client.
 */
export const eventsApi = {
  /**
   * Get all events with optional pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip (default: 0)
   * @param {number} params.limit - Number of records to return (default: 20)
   * @param {string} params.status_filter - Filter by event status
   * @returns {Promise<Object>} List of events with pagination info
   */
  getAll: async (params = {}, config = {}) => {
    const { skip = 0, limit = 20, status_filter } = params;
    const queryParams = { skip, limit };
    if (status_filter) {
      queryParams.status_filter = status_filter;
    }
    const response = await apiClient.get(API_ROUTES.EVENTS.LIST, { ...config, params: queryParams });
    return response.data;
  },

  /**
   * Get event by ID
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Event details
   */
  getById: async (id) => {
    const response = await apiClient.get(API_ROUTES.EVENTS.GET_BY_ID(id));
    return response.data;
  },

  /**
   * Get event with all its sessions
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Event details with sessions
   */
  getWithSessions: async (id) => {
    const response = await apiClient.get(API_ROUTES.EVENTS.GET_WITH_SESSIONS(id));
    return response.data;
  },

  /**
   * Create a new event
   * @param {Object} eventData - Event data
   * @param {string} eventData.title - Event title
   * @param {number} eventData.wp_id - WordPress ID
   * @param {number} eventData.faicon_id - Faicon ID (optional)
   * @param {string} eventData.description - Event description (optional)
   * @param {string} eventData.status - Event status (optional)
   * @param {Array<string>} eventData.tags - Event tags (optional)
   * @returns {Promise<Object>} Created event
   */
  create: async (eventData) => {
    const response = await apiClient.post(API_ROUTES.EVENTS.CREATE, eventData);
    return response.data;
  },

  /**
   * Update an event
   * @param {string} id - Event ID
   * @param {Object} eventData - Updated event data
   * @returns {Promise<Object>} Updated event
   */
  update: async (id, eventData) => {
    const response = await apiClient.put(API_ROUTES.EVENTS.UPDATE(id), eventData);
    return response.data;
  },

  /**
   * Delete an event
   * @param {string} id - Event ID
   * @returns {Promise<void>}
   */
  delete: async (id) => {
    const response = await apiClient.delete(API_ROUTES.EVENTS.DELETE(id));
    return response.data;
  },

  // ============================================================================
  // Event Report Methods
  // ============================================================================

  /**
   * Generate event report
   * @param {string} id - Event ID
   * @param {Object} options - Report generation options
   * @param {Array<string>} options.questions - List of questions for Q&A analysis
   * @param {boolean} options.chatbot - Whether to create a chatbot (default: true)
   * @param {boolean} options.qa - Whether to generate Q&A analysis (default: true)
   * @param {boolean} options.graph - Whether to generate knowledge graph (default: true)
   * @returns {Promise<Object>} Report generation response with report_id and status
   */
  generateReport: async (id, options = {}) => {
    const { questions = [], chatbot = true, qa = true, graph = true, push_to_wordpress = false, faicon_post_id } = options;
    const body = {
      questions,
      chatbot,
      qa,
      graph,
      push_to_wordpress,
    };
    // MARKO KOD
    if (faicon_post_id) {
      body.faicon_post_id = faicon_post_id;
    }
    // MARKO KOD KRAJ
    const response = await apiClient.post(API_ROUTES.EVENTS.GENERATE_REPORT(id), body);
    return response.data;
  },

  /**
   * Get event report status
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Report status including progress and current step
   */
  getReportStatus: async (id) => {
    const response = await apiClient.get(API_ROUTES.EVENTS.REPORT_STATUS(id));
    return response.data;
  },

  /**
   * Get generated event report data
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Full event report data
   */
  getReport: async (id) => {
    const response = await apiClient.get(API_ROUTES.EVENTS.REPORT(id));
    return response.data;
  },

  // MARKO KOD
  /**
   * Send event report to Faicon (same as manual script: POST to Faicon events report API).
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Response with faicon_response
   */
  publishToFaicon: async (id) => {
    const response = await apiClient.post(API_ROUTES.EVENTS.SEND_FAICON(id));
    return response.data;
  },
  // MARKO KOD KRAJ

  /**
   * Publish event report to WordPress
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Response with wordpress_response
   */
  publishToWordPress: async (id) => {
    const response = await apiClient.post(API_ROUTES.EVENTS.SEND_WORDPRESS(id));
    return response.data;
  },

  // MARKO KOD
  /**
   * Send event report to Faicon (same as manual script: POST to Faicon events report API).
   * @param {string} id - Event ID
   * @returns {Promise<Object>} Response with faicon_response
   */
  publishToFaicon: async (id) => {
    const response = await apiClient.post(API_ROUTES.EVENTS.SEND_FAICON(id));
    return response.data;
  },
  // MARKO KOD KRAJ

  /**
   * Download event report as PDF
   * @param {string} id - Event ID
   * @param {string} filename - Optional filename for the downloaded file
   * @returns {Promise<void>} Triggers file download
   */
  downloadReportPdf: async (id, filename = null) => {
    const response = await apiClient.get(
      `/api/reports/events/${id}/report/pdf`,
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header if not provided
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        filename = `event_report_${id}.pdf`;
      }
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// Export individual functions for backward compatibility
export const {
  getAll,
  getById,
  getWithSessions,
  create,
  update,
  delete: deleteEvent,
  generateReport,
  getReportStatus,
  getReport,
  publishToWordPress,
  downloadReportPdf,
} = eventsApi;

export default eventsApi;

